import '../styles/Policies/Datausagepolicy.css';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const pillars = [
  {
    id: 'no-cookies',
    icon: '◎',
    title: 'No Cookies',
    headline: 'We do not use cookies on this site',
    body: `Actyra does not place any cookies — including session cookies, persistent cookies, or third-party tracking cookies — on your device when you visit our website. You will never encounter a cookie consent banner on this platform. This is an intentional architectural decision.`,
  },
  {
    id: 'no-tracking',
    icon: '⊘',
    title: 'No Behavioural Tracking',
    headline: 'Your browsing is not tracked or profiled',
    body: `We do not use advertising pixels, cross-site tracking scripts, social media trackers, fingerprinting technologies, or any third-party behavioural analytics tools. We do not build profiles of visitors to this site, and we do not share or sell browsing behaviour data.`,
  },
  {
    id: 'anonymous-analytics',
    icon: '◈',
    title: 'Anonymous Analytics Only',
    headline: 'We collect only anonymised, aggregate data',
    body: `To understand how our research content is accessed and to improve the relevance of our publications, we may collect anonymised, aggregate analytics data. This includes:`,
    items: [
      'Page views and general navigation paths — not tied to any individual',
      'Browser type and device class (desktop, mobile) — no device fingerprinting',
      'General geographic region (country or city level) — not precise location',
      'Referring domain (the site that linked to us) — no cross-session tracking',
    ],
    note: `This data cannot be used to identify you individually. It is processed solely to improve Actyra research delivery and platform performance.`,
  },
  {
    id: 'newsletter-data',
    icon: '◉',
    title: 'Newsletter & Research Subscriptions',
    headline: 'What we collect when you subscribe',
    body: `When you subscribe to Actyra research publications, actuarial insight briefs, or industry commentary, we collect your email address and, if provided, your name and professional context. This data is:`,
    items: [
      'Used only to deliver the research communications you subscribed to',
      'Never shared with third-party advertisers or data brokers',
      'Stored securely and subject to our data retention policy (see Privacy Policy)',
      'Removable at any time — every communication includes a clear unsubscribe link',
    ],
  },
  {
    id: 'contact-data',
    icon: '◑',
    title: 'Enquiry & Contact Data',
    headline: 'Data from research or advisory enquiries',
    body: `If you contact Actyra regarding IFRS 17 advisory, capital and solvency consulting, model validation, or any other advisory service, we will hold the information you provide (name, email, organisation, enquiry detail) for the purposes of responding and, where an engagement proceeds, fulfilling that engagement. This data is governed fully by our Privacy Policy.`,
  },
  {
    id: 'third-party',
    icon: '◐',
    title: 'No Third-Party Advertising',
    headline: 'We do not run advertising on this platform',
    body: `Actyra does not carry advertising of any kind on this website. We do not participate in advertising networks, retargeting programmes, or data exchange platforms. Research content and advisory services are the exclusive focus of this platform. Our revenue model is built on research credibility and consulting engagements — not on monetising visitor data.`,
  },
  {
    id: 'local-storage',
    icon: '◒',
    title: 'Local Storage',
    headline: 'Minimal, functional use of browser storage',
    body: `In limited cases, the Actyra platform may use browser local storage (not cookies) to preserve your preferences within a single session — for example, your most recently accessed research pillar or dashboard view state. This data:`,
    items: [
      'Exists only in your own browser and is never transmitted to our servers',
      'Is cleared when you close your browser or clear your browser storage',
      'Contains no personally identifiable information',
      'Is used solely to improve your experience on the platform',
    ],
  },
  {
    id: 'your-controls',
    icon: '◓',
    title: 'Your Controls',
    headline: 'You are in control',
    body: `You may manage your data interactions with Actyra at any time:`,
    items: [
      'Unsubscribe from research communications via the link in any email',
      'Request deletion of your contact or subscription data by emailing "info@actyra.co.ke"',
      'Clear any locally-stored preference data through your browser settings at any time',
      'Opt out of anonymised analytics by using standard browser "Do Not Track" settings or a privacy-focused browser',
    ],
  },
  {
    id: 'contact',
    icon: '◍',
    title: 'Questions',
    headline: 'Contact our data team',
    body: `If you have any questions about how Actyra handles data, or to exercise any of your rights under our Privacy Policy, contact:\n\nActyra — Data\nEmail: info@actyra.co.ke`,
  },
];

export default function DataUsagePolicy() {
  const cardRefs = useRef([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('dup-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    cardRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const effectiveDate = 'May 2025';

  return (
    <div className="dup-root">
      {/* Background */}
      <div className="dup-bg" aria-hidden="true">
        <div className="dup-bg-dots" />
        <div className="dup-bg-radial" />
      </div>

      {/* Header */}
      <header className="dup-header">
        <div className="dup-header-inner">
          <Link to="/" className="dup-back">
            <span>←</span>
            <span>Back to Actyra</span>
          </Link>

          <div className="dup-eyebrow">
            <span className="dup-eyebrow-pill">No Cookies · No Tracking · No Ads</span>
          </div>

          <h1 className="dup-title">Data Usage<br /><em>Policy</em></h1>

          <p className="dup-intro">
            Actyra is built on research credibility. That principle extends to how we treat your data.
            This page explains our approach — simply and completely.
          </p>

          <div className="dup-badges">
            <div className="dup-badge">
              <span className="dup-badge-icon">◎</span>
              <span>Zero cookies</span>
            </div>
            <div className="dup-badge">
              <span className="dup-badge-icon">⊘</span>
              <span>No ad tracking</span>
            </div>
            <div className="dup-badge">
              <span className="dup-badge-icon">◈</span>
              <span>Anon analytics only</span>
            </div>
          </div>

          <div className="dup-effective">Effective {effectiveDate}</div>
        </div>
      </header>

      {/* Cards grid */}
      <main className="dup-main">
        <div className="dup-grid">
          {pillars.map((pillar, i) => (
            <div
              key={pillar.id}
              id={pillar.id}
              className={`dup-card dup-reveal ${i === 0 ? 'dup-card--featured' : ''}`}
              ref={(el) => (cardRefs.current[i] = el)}
              style={{ transitionDelay: `${(i % 3) * 0.1}s` }}
            >
              <div className="dup-card-top">
                <span className="dup-card-icon">{pillar.icon}</span>
                <span className="dup-card-tag">{pillar.title}</span>
              </div>
              <h2 className="dup-card-headline">{pillar.headline}</h2>
              <p className="dup-card-body">
                {pillar.body.split('\n\n').map((para, j, arr) => (
                  <span key={j}>
                    {para}
                    {j < arr.length - 1 && <><br /><br /></>}
                  </span>
                ))}
              </p>
              {pillar.items && (
                <ul className="dup-card-list">
                  {pillar.items.map((item, ii) => (
                    <li key={ii} className="dup-card-list-item">
                      <span className="dup-check">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
              {pillar.note && (
                <div className="dup-card-note">{pillar.note}</div>
              )}
              <div className="dup-card-glow" />
            </div>
          ))}
        </div>

        <div className="dup-footer-row">
          <Link to="/privacy" className="dup-footer-link">← Privacy Policy</Link>
          <span className="dup-footer-copy">© 2025 Actyra. All rights reserved.</span>
          <Link to="/terms" className="dup-footer-link">Terms of Use →</Link>
        </div>
      </main>
    </div>
  );
}
